/**
 * Cart drawer suggestions panel (snippets/cart-drawer-side.liquid).
 * - Product pages record their handle as recently viewed (the key the Recently
 *   viewed section uses).
 * - Suggestions come from the product recommendations endpoint for the first
 *   cart item: complementary products first, related ones when there are none.
 * - Recently viewed products render one card each from their product URL.
 * Cards (the theme's product card) come from sections/cart-drawer-suggestions.liquid
 * and are cached here: the drawer re-renders after every cart change, and the
 * panel refills from the cache at once instead of loading again.
 */
(function () {
  'use strict';

  if (window.AxiomCartDrawerSide) return;

  var VIEWED_KEY = 'axiom_recently_viewed';
  var VIEWED_MAX = 12;
  var recsCache = {};
  var viewedCache = {};
  var chosenTab = null;
  // The product page being viewed. Kept from the first render: the drawer's
  // later renders come from cart requests, which are not product pages.
  var currentHandle = '';
  var wide = window.matchMedia('(min-width: 1024px)');

  function drawer() {
    return document.getElementById('cart-drawer');
  }

  function side() {
    var d = drawer();
    return d ? d.querySelector('[data-cart-side]') : null;
  }

  function root() {
    var routes = window.Axiom && window.Axiom.routes;
    return (routes && routes.root) || '/';
  }

  function readViewed() {
    try {
      var list = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  }

  function recordView() {
    var s = side();
    currentHandle = (s && s.dataset.viewedCurrent) || '';
    var handle = s && s.dataset.viewed === 'true' ? currentHandle : '';
    if (!handle) return;
    var list = readViewed().filter(function (item) { return item && item !== handle; });
    try {
      localStorage.setItem(VIEWED_KEY, JSON.stringify([handle].concat(list).slice(0, VIEWED_MAX)));
    } catch (error) {
      /* Storage unavailable: nothing to record. */
    }
  }

  function cartHandles(s) {
    return (s.dataset.cartHandles || '').split(',').filter(Boolean);
  }

  function itemsFrom(html) {
    if (!html) return '';
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var list = doc.querySelector('[data-cart-side-items]');
    return list ? list.innerHTML.trim() : '';
  }

  // Fills a panel's list, leaving out products that are in the cart now.
  function fill(panel, html, inCart) {
    var list = panel.querySelector('[data-cart-side-items]');
    if (!list) return false;
    list.innerHTML = html || '';
    list.querySelectorAll('[data-cart-side-card]').forEach(function (card) {
      if (inCart.indexOf(card.dataset.productHandle) !== -1) card.remove();
    });
    var ready = Boolean(list.querySelector('[data-cart-side-card]'));
    panel.dataset.ready = ready ? 'true' : '';
    // The cards are the theme's product cards: wire up their galleries,
    // swatches and quick view.
    if (ready && window.AxiomProductCard && typeof window.AxiomProductCard.initialize === 'function') {
      window.AxiomProductCard.initialize(list);
    }
    return ready;
  }

  /* ---------------- Suggestions ---------------- */
  function fetchSection(url) {
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(itemsFrom)
      .catch(function () { return ''; });
  }

  function loadRecs(s) {
    var panel = s.querySelector('[data-cart-side-panel="recs"]');
    if (!panel) return;
    var inCart = cartHandles(s);
    var list = panel.querySelector('[data-cart-side-items]');
    if (list && list.hasAttribute('data-cart-side-static')) {
      panel.dataset.ready = list.querySelector('[data-cart-side-card]') ? 'true' : '';
      return;
    }
    var url = s.dataset.recsUrl;
    if (!url) {
      fill(panel, '', inCart);
      return;
    }
    if (Object.prototype.hasOwnProperty.call(recsCache, url)) {
      if (typeof recsCache[url] === 'string') fill(panel, recsCache[url], inCart);
      return;
    }
    recsCache[url] = fetchSection(url + '&intent=complementary').then(function (html) {
      return html || fetchSection(url + '&intent=related');
    }).then(function (html) {
      recsCache[url] = html;
      var current = side();
      if (current && current.dataset.recsUrl === url) {
        fill(current.querySelector('[data-cart-side-panel="recs"]'), html, cartHandles(current));
        sync(current);
      }
    });
  }

  /* ---------------- Recently viewed ---------------- */
  function viewedHandles(s) {
    var inCart = cartHandles(s);
    return readViewed().filter(function (handle) {
      return handle && handle !== currentHandle && inCart.indexOf(handle) === -1;
    }).slice(0, 8);
  }

  function viewedHtml(handles) {
    return handles.map(function (handle) {
      return typeof viewedCache[handle] === 'string' ? viewedCache[handle] : '';
    }).join('');
  }

  function loadViewed(s) {
    var panel = s.querySelector('[data-cart-side-panel="viewed"]');
    if (!panel) return;
    var handles = viewedHandles(s);
    var missing = handles.filter(function (handle) {
      return !Object.prototype.hasOwnProperty.call(viewedCache, handle);
    });
    fill(panel, viewedHtml(handles), cartHandles(s));
    if (!missing.length) return;
    Promise.all(missing.map(function (handle) {
      var url = root() + 'products/' + encodeURIComponent(handle) + '?section_id=cart-drawer-suggestions';
      viewedCache[handle] = fetchSection(url).then(function (html) {
        viewedCache[handle] = html;
      });
      return viewedCache[handle];
    })).then(function () {
      var current = side();
      if (!current) return;
      var currentPanel = current.querySelector('[data-cart-side-panel="viewed"]');
      if (currentPanel) fill(currentPanel, viewedHtml(viewedHandles(current)), cartHandles(current));
      sync(current);
    });
  }

  /* ---------------- Tabs and placement ---------------- */
  function selectTab(s, name, animate) {
    s.querySelectorAll('[data-cart-side-tab]').forEach(function (tab) {
      var on = tab.dataset.cartSideTab === name;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });
    s.querySelectorAll('[data-cart-side-panel]').forEach(function (panel) {
      var show = panel.dataset.cartSidePanel === name;
      if (show && panel.hidden && animate) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
      }
      panel.hidden = !show;
    });
  }

  // Shows the tabs whose lists have products; the panel shows when one has.
  function sync(s) {
    if (!s) return;
    var ready = Array.prototype.filter.call(s.querySelectorAll('[data-cart-side-tab]'), function (tab) {
      var panel = s.querySelector('[data-cart-side-panel="' + tab.dataset.cartSideTab + '"]');
      var ok = Boolean(panel && panel.dataset.ready === 'true');
      tab.hidden = !ok;
      return ok;
    });
    var current = ready.find(function (tab) { return tab.dataset.cartSideTab === chosenTab; }) || ready[0];
    selectTab(s, current ? current.dataset.cartSideTab : null, false);
    s.classList.toggle('is-single', ready.length === 1);
    s.hidden = ready.length === 0;
    var d = drawer();
    if (!d) return;
    d.classList.toggle('has-side', ready.length > 0 && wide.matches);
    // The tabs take the header's height so the two rules meet as one line.
    var header = d.querySelector('.cart-drawer__header');
    if (header && header.offsetHeight) s.style.setProperty('--cart-side-head', header.offsetHeight + 'px');
  }

  // Desktop: beside the drawer. Smaller screens: under the cart items.
  function place(s) {
    var d = drawer();
    if (!d || !s) return;
    if (wide.matches) {
      if (s.parentElement !== d) d.appendChild(s);
      return;
    }
    var target = d.querySelector('[data-cart-drawer-scroll-region]') || d.querySelector('[data-cart-drawer-body]');
    if (target && s.parentElement !== target) target.appendChild(s);
  }

  function refresh() {
    var s = side();
    if (!s) return;
    place(s);
    loadRecs(s);
    loadViewed(s);
    sync(s);
  }

  document.addEventListener('click', function (event) {
    var tab = event.target.closest && event.target.closest('[data-cart-side-tab]');
    if (!tab) return;
    var s = tab.closest('[data-cart-side]');
    chosenTab = tab.dataset.cartSideTab;
    if (s) selectTab(s, chosenTab, true);
  });

  document.addEventListener('keydown', function (event) {
    var tab = event.target.closest && event.target.closest('[data-cart-side-tab]');
    if (!tab || (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) return;
    var tabs = Array.prototype.filter.call(tab.parentElement.querySelectorAll('[data-cart-side-tab]'), function (item) {
      return !item.hidden;
    });
    var step = (event.key === 'ArrowRight') === (document.documentElement.dir !== 'rtl') ? 1 : tabs.length - 1;
    var next = tabs[(tabs.indexOf(tab) + step) % tabs.length];
    if (!next) return;
    event.preventDefault();
    chosenTab = next.dataset.cartSideTab;
    selectTab(tab.closest('[data-cart-side]'), chosenTab, true);
    next.focus();
  });

  wide.addEventListener('change', refresh);
  document.addEventListener('theme:cart-drawer:open', refresh);
  document.addEventListener('cart:drawer:rendered', refresh);
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('#cart-drawer')) refresh();
  });

  function start() {
    recordView();
    // Load the lists while the page is idle so the first opening has them.
    (window.requestIdleCallback || function (callback) { return window.setTimeout(callback, 1200); })(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.AxiomCartDrawerSide = { refresh: refresh };
})();
